import * as Enumerable from "linq";
import {
    MepDashboardEmissionsBySomeTypeItem, MepDashboardRequest, MepGetDashboardEmissionsBySomeTypeRequest,
    EmissionTypeCategoryEnum,
} from "../../../../../../../../models/mep/mep.models";
import { MepService } from "../../../../../../../../services/mep/mep.service";
import { MepDashboardFilter } from "../mep-dashboard-filter/mep-dashboard-filter";
import { EmissionsByTypeTabEnum, EmissionsByTypeTableForPrintingViewModel, EmissionsByTypeTableViewModel } from "../mep-emissions-by-type/mep-emissions-by-type";
import { Reflection } from "../../../../../../../../utilities/reflection";
import { computedFrom } from "aurelia-binding";
import { useView } from "aurelia-framework";

@useView('views/pages/modules/mep/tabs/mep-dashboard/partials/mep-emissions-by-cargo-type/mep-emissions-by-cargo-type.html')
export class EmissionsByCargoTypeTabViewModel {

    isEmissionsByTypeLoading: boolean = false;
    allItems: {
        cargoTypeGroupTable: EmissionsByTypeTableViewModel,
        cargoTypesTable: EmissionsByTypeTableViewModel,
        isExpanded: boolean,
        emissionTypeCategoryId: number,
        isGhgEmissions: boolean
    }[];
    filteredItems: {
        cargoTypeGroupTable: EmissionsByTypeTableViewModel,
        cargoTypesTable: EmissionsByTypeTableViewModel,
        isExpanded: boolean,
        emissionTypeCategoryId: number,
        isGhgEmissions: boolean
    }[];
    tableForPrintingViewModel: EmissionsByTypeTableForPrintingViewModel;
    cargoTypeEmissionsData: MepDashboardEmissionsBySomeTypeItem[];
    cargoTypeGroupEmissionsData: MepDashboardEmissionsBySomeTypeItem[];
    title: string = "Emissions by Cargo Type";
    isMepTrial: boolean = false;
    isGhgEmissions: boolean = true;
    supportsCargoType: boolean;

    changeEmissionTypesFilterFunc: (emissionTypeIds: number[]) => void;
    changePointOfInterestsFilterFunc: (pointOfInterestIds: number[]) => void;
    changeVesselTypesFilterFunc: (vesselTypeIds: number[]) => void;
    changeOperationModesFilterFunc: (operationModesIds: number[]) => void;
    changeCargoTypesFilterFunc: (cargoTypeIds: number[]) => void;
    changeCargoTypeGroupsFilterFunc: (cargoTypeGroups: string[]) => void;
    applyFilterFunc: () => void;

    @computedFrom(Reflection.getPropertyPath<EmissionsByCargoTypeTabViewModel>(x => x.filterViewModel.selectedPointsOfInterest),
        Reflection.getPropertyPath<EmissionsByCargoTypeTabViewModel>(x => x.filterViewModel.selectedVesselTypeGroups),
        Reflection.getPropertyPath<EmissionsByCargoTypeTabViewModel>(x => x.filterViewModel.selectedOperationModes),
        Reflection.getPropertyPath<EmissionsByCargoTypeTabViewModel>(x => x.supportsCargoType))
    get isVisible(): boolean {
        const lastRequest = this.filterViewModel.getLastRequest();
        return lastRequest.cargoTypeIds && lastRequest.cargoTypeIds.length != 1 && this.supportsCargoType;
    }

    constructor(private filterViewModel: MepDashboardFilter, private mepService: MepService,
        public portId: number, supportsCargoType: boolean) {
        this.supportsCargoType = supportsCargoType;
        this.changeEmissionTypesFilterFunc = (emissionsTypeIds: number[]) => this.filterViewModel.changeEmissionTypesFilter(emissionsTypeIds);
        this.changePointOfInterestsFilterFunc = (pointOfInterestIds: number[]) => this.filterViewModel.changePointOfInterestsFilter(pointOfInterestIds);
        this.changeVesselTypesFilterFunc = (vesselTypeIds: number[]) => this.filterViewModel.changeVesselTypesFilter(vesselTypeIds);
        this.changeOperationModesFilterFunc = (operationModesIds: number[]) => this.filterViewModel.changeOperationModesFilter(operationModesIds);
        this.changeCargoTypesFilterFunc = (cargoTypeIds: number[]) => this.filterViewModel.changeCargoTypeFilter(cargoTypeIds);
        this.changeCargoTypeGroupsFilterFunc = (cargoTypeGroups: string[]) => this.filterViewModel.changeCargoTypeGroupFilter(cargoTypeGroups);
        this.applyFilterFunc = () => this.filterViewModel.applyAllFilters();
    }

    getTabData(request: MepDashboardRequest) {
        return new Promise<string>((resolve, reject) => {

            if (this.filterViewModel.emissionTypeCategoryIds.length == 1) {
                this.isGhgEmissions = this.filterViewModel.emissionTypeCategoryIds[0] == EmissionTypeCategoryEnum.GhgEmissions;
            }

            this.isEmissionsByTypeLoading = true;

            const getMepEmissionsBySomeTypePromise = this.getMepEmissionsBySomeType(request);

            // we want to resolve before so that the dashboard does not wait for the heatmap to load 
            getMepEmissionsBySomeTypePromise.then(() => {
                this.isEmissionsByTypeLoading = false;
                resolve(null);
            }).catch(() => {
                this.isEmissionsByTypeLoading = false;
                reject(`MEP ${this.title} - Error retrieving the data!`);
            });
        });
    }

    getMepEmissionsBySomeType(request: MepDashboardRequest) {
        return new Promise((resolve, reject) => {
            const cargoTypeEmissionsRequest: MepGetDashboardEmissionsBySomeTypeRequest = {
                emissionTypeIds: request.emissionTypeIds,
                operationModeIds: request.operationModeIds,
                pointOfInterestIds: request.pointOfInterestIds,
                cargoTypeIds: request.cargoTypeIds,
                portId: request.portId,
                vesselTypeGroupIds: request.vesselTypeGroupIds,
                vesselTypeIds: request.vesselTypeIds,
                year: request.year,
                secondaryTypeId: EmissionsByTypeTabEnum.CargoType,
                months: request.months
            }

            const cargoTypeGroupEmissionsRequest: MepGetDashboardEmissionsBySomeTypeRequest = {
                emissionTypeIds: request.emissionTypeIds,
                operationModeIds: request.operationModeIds,
                pointOfInterestIds: request.pointOfInterestIds,
                cargoTypeIds: request.cargoTypeIds,
                portId: request.portId,
                vesselTypeGroupIds: request.vesselTypeGroupIds,
                vesselTypeIds: request.vesselTypeIds,
                year: request.year,
                secondaryTypeId: EmissionsByTypeTabEnum.CargoTypeGroup,
                months: request.months
            }

            const promise1 = this.mepService.getMepEmissionsBySomeType(cargoTypeEmissionsRequest);
            const promise2 = this.mepService.getMepEmissionsBySomeType(cargoTypeGroupEmissionsRequest);
            Promise.all([promise1, promise2]).then(results => {
                this.cargoTypeEmissionsData = results[0].results;
                this.cargoTypeGroupEmissionsData = results[1].results;
                this.initTabData();
                resolve(null);
            }).catch(() => {
                reject();
            });
        });
    }

    initTabData() {
        if (!this.cargoTypeEmissionsData || this.cargoTypeEmissionsData.length == 0)
            return;

        const items: {
            cargoTypeGroupTable: EmissionsByTypeTableViewModel,
            cargoTypesTable: EmissionsByTypeTableViewModel,
            emissionTypeCategoryId: number,
            isGhgEmissions: boolean,
            isExpanded: boolean
        }[] = [];

        this.allItems = [];
        this.filteredItems = [];
        Enumerable.from(this.cargoTypeGroupEmissionsData).groupBy(x => x.emissionTypeCategoryId).forEach(g => {
            Enumerable.from(g)
                .groupBy(x => x.secondaryTypeName)
                .forEach(group => {

                    const cargoTypeEmissionsDataFiltered = this.cargoTypeEmissionsData.filter(x => x.secondaryTypeGroupName == group.key() && x.emissionTypeCategoryId == g.key());

                    const cargoTypeGroupTable = new EmissionsByTypeTableViewModel(this.applyFilterFunc, this.changeEmissionTypesFilterFunc, this.changePointOfInterestsFilterFunc,
                        this.changeVesselTypesFilterFunc, this.changeOperationModesFilterFunc, this.changeCargoTypesFilterFunc, this.changeCargoTypeGroupsFilterFunc, EmissionsByTypeTabEnum.CargoTypeGroup, group.toArray(), group.concat(cargoTypeEmissionsDataFiltered).toArray());
                    cargoTypeGroupTable.initTable();

                    const cargoTypeTable = new EmissionsByTypeTableViewModel(this.applyFilterFunc, this.changeEmissionTypesFilterFunc, this.changePointOfInterestsFilterFunc,
                        this.changeVesselTypesFilterFunc, this.changeOperationModesFilterFunc, this.changeCargoTypesFilterFunc, this.changeCargoTypeGroupsFilterFunc, EmissionsByTypeTabEnum.CargoType, cargoTypeEmissionsDataFiltered, group.concat(cargoTypeEmissionsDataFiltered).toArray());
                    cargoTypeTable.initTable();

                    items.push({
                        cargoTypeGroupTable: cargoTypeGroupTable,
                        cargoTypesTable: cargoTypeTable,
                        isExpanded: true,
                        emissionTypeCategoryId: g.key(),
                        isGhgEmissions: g.key() == EmissionTypeCategoryEnum.GhgEmissions
                    });
                });
        });
        this.allItems = items;
        this.filteredItems = items.filter(x => this.isGhgEmissions ? x.emissionTypeCategoryId == EmissionTypeCategoryEnum.GhgEmissions : x.emissionTypeCategoryId == EmissionTypeCategoryEnum.CriteriaPollutants);

    }

    clearEmissionsData() {
        this.cargoTypeEmissionsData = [];
        this.cargoTypeGroupEmissionsData = [];
        this.filteredItems = [];
        this.allItems = [];
    }

    setIsGhgEmission = (value: boolean) => {
        this.isGhgEmissions = value;
        this.initTabData();
    }

    toggleCargoTypeGroupExpander(group: { cargoTypeGroupTable: EmissionsByTypeTableViewModel, cargoTypesTable: EmissionsByTypeTableViewModel, isExpanded: boolean }) {
        const grp = this.filteredItems.filter(x => x == group)[0];
        grp.isExpanded = !grp.isExpanded;
    }
}